import { useState, useCallback } from 'react';
import {
  ordisAPIService,
  APIRequestError,
  ConfirmationDetails,
  ConfirmationDecision,
} from '../service/ordisApiService';

export interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  downloadUrl?: string;
  // When the bot kicks off an async background operation it returns a
  // line of the form ``job_id: <uuid>`` in its response. The chat hook
  // extracts that value and attaches it here so the renderer can mount
  // an in-place status card that polls the backend until the job
  // resolves. Absent on regular messages.
  jobId?: string;
  // HITL: present when a high-risk tool paused for human confirmation. The
  // renderer mounts a ConfirmationCard with Approve/Deny buttons. The token
  // is single-use and bound server-side to the verified caller.
  confirmation?: {
    token: string;
    details: ConfirmationDetails;
  };
}

const INITIAL_BOT_MESSAGE: ChatMessage = {
  id: 1,
  text: "Hello! I'm GFT Ordis, your AI-powered L1 Operations Assistant. I can help you with:\n\n• Starting and stopping TM1 instances\n• Creating TM1 instance backups\n• Permission management\n\nHow can I assist you today?",
  sender: 'bot',
  timestamp: new Date()
};

const ERROR_MESSAGES = {
  TECHNICAL_DIFFICULTY: "I apologize, but I'm experiencing some technical difficulties right now. Please try again in a moment, or contact the GFT Infrastructure team at gft-infra-services@amazon.com if the issue persists.",
  NETWORK_ERROR: "Unable to connect to the server. Please check your connection and try again.",
  TIMEOUT_ERROR: "The request timed out. Please try again.",
  AUTH_ERROR: "Authentication failed. Please refresh the page and try again.",
  RATE_LIMITED: "You're sending messages too quickly. Please wait a moment before trying again.",
  INPUT_BLOCKED: "Your message was not accepted. Please rephrase your request and try again."
} as const;

export const useMessages = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_BOT_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const getErrorMessage = (error: unknown): string => {
    // Backend-shaped errors: the API service preserves the server's `error`
    // text. Use it for authorization (403) and other 4xx responses so the
    // user sees the real reason — RBAC scope, prod gate, operation denial,
    // PII block, malicious-pattern reject, etc. — instead of a generic
    // "refresh the page" message.
    if (error instanceof APIRequestError) {
      if (error.status === 401) return ERROR_MESSAGES.AUTH_ERROR;
      if (error.status === 429) return ERROR_MESSAGES.RATE_LIMITED;
      if (error.serverMessage) return error.serverMessage;
      if (error.status === 403) return 'Access denied.';
      if (error.status === 400) return ERROR_MESSAGES.INPUT_BLOCKED;
    }

    if (error instanceof Error) {
      if (error.message.includes('timeout')) return ERROR_MESSAGES.TIMEOUT_ERROR;
      if (error.message.includes('network') || error.message.includes('fetch')) return ERROR_MESSAGES.NETWORK_ERROR;
      // Last-resort heuristic for legacy Error throws (e.g. the 401
      // redirect path in the API service).
      if (error.message.includes('401')) return ERROR_MESSAGES.AUTH_ERROR;
    }
    return ERROR_MESSAGES.TECHNICAL_DIFFICULTY;
  };

  const sendMessage = useCallback(async (text: string) => {
    const userMessage: ChatMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date()
    };

    setIsLoading(true);
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await ordisAPIService.resolveQuery({ query: text });

      // HITL: a high-risk tool paused for confirmation. Attach the verified
      // details + single-use token so the renderer mounts a ConfirmationCard
      // instead of a plain answer. No job_id parsing on this branch — there
      // is no free-text answer yet.
      if (response.requiresConfirmation && response.confirmationToken && response.confirmation) {
        const confirmMessage: ChatMessage = {
          id: Date.now() + 1,
          text: 'Please review and confirm this action before it runs.',
          sender: 'bot',
          timestamp: new Date(response.timestamp),
          confirmation: {
            token: response.confirmationToken,
            details: response.confirmation,
          },
        };
        setMessages((prev) => [...prev, confirmMessage]);
        setIsLoading(false);
        return response;
      }

      const responseText = response.response ?? '';

      // Detect a job_id in the bot's reply so the renderer can attach an
      // inline JobStatusCard. The agent paraphrases the Lambda response
      // many ways, so we accept up to 3 short connector words between a
      // ``job_id`` / ``job id`` token and the UUID. Examples that match:
      //   "job_id: <uuid>"
      //   "The job ID is <uuid>"
      //   "Job ID was <uuid>"
      //   "Job ID is now <uuid>"
      //   "Job ID = <uuid>"
      //   "Your job ID, <uuid>, ..."
      //
      // We still capture only a 36-character UUID (8-4-4-4-12 hex), so a
      // stray hex string elsewhere in the message can't trigger a false
      // positive — the leading ``job id`` token is required.
      const jobIdMatch = responseText.match(
        /\bjob[\s_]?id\b(?:\W+\w{1,8}){0,3}\W+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i,
      );
      const extractedJobId = jobIdMatch ? jobIdMatch[1].toLowerCase() : undefined;

      const botResponse: ChatMessage = {
        id: Date.now() + 1,
        text: responseText,
        sender: 'bot',
        timestamp: new Date(response.timestamp),
        downloadUrl: response.downloadUrl,
        jobId: extractedJobId,
      };

      setMessages(prev => [...prev, botResponse]);
      setIsLoading(false);
      
      // Return response so caller can extract userAlias
      return response;
    } catch (error) {
      console.error('API Error:', error);
      
      const errorResponse: ChatMessage = {
        id: Date.now() + 1,
        text: getErrorMessage(error),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorResponse]);
      setIsLoading(false);
      return null;
    }
  }, []);

  const respondToConfirmation = useCallback(
    async (token: string, decision: ConfirmationDecision) => {
      setIsLoading(true);
      try {
        const response = await ordisAPIService.confirmAction(token, decision);
        const botResponse: ChatMessage = {
          id: Date.now() + 1,
          text:
            response.response ||
            (decision === 'approve'
              ? 'Action approved.'
              : 'Cancelled — no action was taken.'),
          sender: 'bot',
          timestamp: new Date(response.timestamp || Date.now()),
          downloadUrl: response.downloadUrl,
        };
        setMessages((prev) => [...prev, botResponse]);
      } catch (error) {
        console.error('Confirmation error:', error);
        const errorResponse: ChatMessage = {
          id: Date.now() + 1,
          text: getErrorMessage(error),
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { messages, isLoading, sendMessage, respondToConfirmation, rateLimitedUntil };
};
